import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Text from "../../../../utils/Custom/text";
import CommonForm from "../../../../utils/commonComponent/commonForm";
import { CancelButton, SaveAndUpdate } from "../../../../utils/Custom/Button";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import axiosWithToken from "../../../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../../../global";
import { getPermissions } from "../../../Pages/rbac/hasAccess";
import { validateRequiredFields } from "../../../../utils/commonComponent/errorAlert";
import { Button, Tooltip, IconButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PreviewIcon from "@mui/icons-material/Preview";
import DeleteIcon from "@mui/icons-material/Delete";

const EditCandidate = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const navigate = useNavigate();

  const [inputStates, setInputStates] = React.useState({
    status: 1,
    dateAdded: new Date().toISOString().split("T")[0],
  });

  const [disciplines, setDisciplines] = React.useState([]);
  const [employmentTypes, setEmploymentTypes] = React.useState([]);
  const [employmentPrefs, setEmploymentPrefs] = React.useState([]);
  const [sourceChannels, setSourceChannels] = React.useState([]);

  const updateInputObject = (key, value) => {
    setInputStates((prev) => ({ ...prev, [key]: value }));
  };

  const fetchNextCandId = async () => {
    try {
      const [last] = await fetchQuery(
        `SELECT CandId FROM talentAcqCandidate ORDER BY id DESC LIMIT 1`
      );
      const lastId = parseInt(last?.CandId?.match(/\d+$/)?.[0] || "0", 10);
      const nextId = `CAND-${String(lastId + 1).padStart(3, "0")}`;
      updateInputObject("CandId", nextId);
    } catch (error) {
      console.error("Error fetching last Cand ID:", error);
      updateInputObject("CandId", "CAND-001");
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const masters = await fetchQuery(
        "SELECT id, type, Value FROM talentAcquisitionMaster WHERE status != -1"
      );
      const list = masters || [];

      setDisciplines(list.filter((m) => m.type == 1).map((m) => ({ id: m.id, name: m.Value })));
      setEmploymentTypes(list.filter((m) => m.type == 2).map((m) => ({ id: m.id, name: m.Value })));
      setSourceChannels(list.filter((m) => m.type == 8).map((m) => ({ id: m.id, name: m.Value })));
      setEmploymentPrefs(list.filter((m) => m.type == 12).map((m) => ({ id: m.id, name: m.Value })));
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  const fetchDetails = async () => {
    try {
      const [record] = await fetchQuery(
        `SELECT * FROM talentAcqCandidate WHERE id = ${params.id}`
      );
      if (record) {
        if (record.dateAdded) record.dateAdded = record.dateAdded.split("T")[0];
        setInputStates(record);
      }
    } catch (error) {
      console.error("Error fetching candidate data:", error);
    }
  };

  const onSubmit = async () => {
    const fieldsToValidate = [
      { value: inputStates.CandId, label: "Cand ID is required" },
      { value: inputStates.dateAdded, label: "Date Added is required" },
      { value: inputStates.candidateName, label: "Candidate Name is required" },
      { value: inputStates.discipline, label: "Discipline is required" },
      { value: inputStates.mobileNo, label: "Mobile No. is required" },
      { value: inputStates.emaiId, label: "Email ID is required" },
    ];

    if (!validateRequiredFields(fieldsToValidate)) return;

    const mobileRegex = /^\d{10}$/;
    if (inputStates.mobileNo && !mobileRegex.test(inputStates.mobileNo)) {
      toast.error("Mobile number must be exactly 10 digits");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (inputStates.emaiId && !emailRegex.test(inputStates.emaiId)) {
      toast.error("Invalid email format");
      return;
    }

    try {
      const url = BASE_URL + `talentAcqCandidate`;
      params.id != 0
        ? await axiosWithToken.patch(`${url}/${params.id}`, inputStates)
        : await axiosWithToken.post(url, inputStates);

      toast.success(
        params.id != 0 ? "Candidate Updated Successfully!" : "Candidate Created Successfully!"
      );
      if (params.id == 0) {
        setTimeout(() => navigate("/talent-acquisition/candidates"), 1000);
      } else {
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  useEffect(() => {
    fetchDropdownOptions();
    if (params.id == 0) fetchNextCandId();
  }, []);

  useEffect(() => {
    if (params.id != 0) fetchDetails();
  }, [params]);

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (/[&#$*]/.test(file.name)) {
      toast.warning("File name contains special characters. Please rename and re-upload.");
      return;
    }
    const data = new FormData();
    data.append("fileToUpload", file);
    axiosWithToken
      .post(BASE_URL + "fileUpload/fileUpload.php", data)
      .then((res) => {
        if (res.status === 200) {
          updateInputObject("resumeFile", res.data);
          toast.success("Resume uploaded successfully!");
        }
      })
      .catch(() => toast.error("Upload failed!"));
  };

  const inputFields = [
    {
      type: "TextField",
      label: "Cand ID *",
      stateKey: "CandId",
      disabled: true,
    },
    {
      type: "DatePicker",
      label: "Date Added *",
      stateKey: "dateAdded",
    },
    {
      type: "TextField",
      label: "Candidate Name *",
      stateKey: "candidateName",
    },
    {
      type: "Autocomplete",
      label: "Gender",
      stateKey: "gender",
      options: [
        { name: "Male" },
        { name: "Female" },
      ],
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Discipline *",
      stateKey: "discipline",
      options: disciplines,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "TextField",
      label: "Education",
      stateKey: "education",
    },
    {
      type: "TextField",
      label: "Certifications",
      stateKey: "certifications",
    },
    {
      type: "TextField",
      label: "Total Exp (Yrs)",
      stateKey: "totalExp",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "TPI/Inspection Exp (Yrs)",
      stateKey: "tpiExp",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Current Employer",
      stateKey: "currentEmployer",
    },
    {
      type: "TextField",
      label: "Current Designation",
      stateKey: "currentDesignation",
    },
    {
      type: "TextField",
      label: "Current CTC (₹ LPA)",
      stateKey: "currentCtc",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Expected CTC (₹ LPA)",
      stateKey: "expectedCtc",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Notice Period",
      stateKey: "noticePeriod",
    },
    {
      type: "TextField",
      label: "Current Location",
      stateKey: "currentLocation",
    },
    {
      type: "Autocomplete",
      label: "Employment Type",
      stateKey: "employmentType",
      options: employmentTypes,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Employment Preference",
      stateKey: "EmploymentPref",
      options: employmentPrefs,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Source Channel",
      stateKey: "sourceChannel",
      options: sourceChannels,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "phoneNo",
      label: "Mobile No. *",
      stateKey: "mobileNo",
    },
    {
      type: "emailId",
      label: "Email ID *",
      stateKey: "emaiId",
    },
    {
      type: "Textarea",
      label: "Notes / Remarks",
      stateKey: "notes",
    },
  ];

  return (
    <div>
      <Text variant="h3" bold className="text-blue-600">
        {params.id == 0 ? "Create Candidate" : "Update Candidate"}
      </Text>
      <br />
      <CommonForm
        inputStates={inputStates}
        updateInputObject={updateInputObject}
        inputFields={inputFields}
      />
      <br />

      {/* Resume Upload */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", marginBottom: "8px" }}>
        <Button
          component="label"
          variant="contained"
          startIcon={<CloudUploadIcon />}
          size="small"
        >
          Upload Resume
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: "none" }}
            onChange={handleResumeUpload}
          />
        </Button>

        {inputStates.resumeFile && (
          <>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<PreviewIcon />}
              href={inputStates.resumeFile}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
            >
              View File
            </Button>
            <Tooltip title="Delete File">
              <IconButton
                size="small"
                sx={{
                  color: "#dc3545",
                  padding: "6px",
                  borderRadius: "50%",
                  transition: "0.2s",
                  "&:hover": { backgroundColor: "#ffe5e5", transform: "scale(1.1)" },
                }}
                onClick={() => updateInputObject("resumeFile", null)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </div>
      <br />
      <div className="flex gap-10 mt-5 justify-end">
        <CancelButton
          id="btn-cancel"
          label="Cancel"
          outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.allAccess) && (
          <SaveAndUpdate
            id="btn-save"
            label={params.id == 0 ? "Save" : "Update"}
            onClick={onSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default EditCandidate;
